import * as yup from "yup";
import type { DefaultValues } from "react-hook-form";
import { EWeightUnit } from "enum";
import { ELanguageCode } from "enum/ELanguageCode";

const emptyToUndefined = (value: unknown, originalValue: unknown) =>
  originalValue === "" || originalValue == null ? undefined : value;

const translationSchema = yup.object({
  name: yup.string().trim().required("Name is required"),
  description: yup.string().trim().required("Description is required"),
  languageCode: yup
    .mixed<ELanguageCode>()
    .oneOf(Object.values(ELanguageCode) as ELanguageCode[], "Invalid language code")
    .required("Language code is required"),
});

export type UpdateProductInfoFormValues = {
  translations: {
    ar: {
      name: string;
      description: string;
      languageCode: ELanguageCode;
    };
    en: {
      name: string;
      description: string;
      languageCode: ELanguageCode;
    };
  };
  weight?: number;
  estimatedWeight?: number;
  drainedWeight?: number;
  weightUnit: EWeightUnit;
  countryOfManufactureISO?: string;
  countryOfRegistrationISO: string;
  barcode: string;
  price: number;
};

export const updateProductInfoSchema: yup.ObjectSchema<UpdateProductInfoFormValues> = yup
  .object({
    translations: yup
      .object({
        ar: translationSchema.shape({
          languageCode: yup
            .mixed<ELanguageCode>()
            .oneOf([ELanguageCode.AR] as ELanguageCode[], "Arabic translation must use 'ar'")
            .required("Language code is required"),
        }),
        en: translationSchema.shape({
          languageCode: yup
            .mixed<ELanguageCode>()
            .oneOf([ELanguageCode.EN] as ELanguageCode[], "English translation must use 'en'")
            .required("Language code is required"),
        }),
      })
      .required("Translations are required"),

    weight: yup
      .number()
      .transform(emptyToUndefined)
      .typeError("Weight must be a number")
      .min(0, "Weight must be greater than or equal to 0")
      .optional(),

    estimatedWeight: yup
      .number()
      .transform(emptyToUndefined)
      .typeError("Estimated weight must be a number")
      .min(0, "Estimated weight must be greater than or equal to 0")
      .optional(),

    drainedWeight: yup
      .number()
      .transform(emptyToUndefined)
      .typeError("Drained weight must be a number")
      .min(0, "Drained weight must be greater than or equal to 0")
      .optional(),

    weightUnit: yup
      .mixed<EWeightUnit>()
      .oneOf(Object.values(EWeightUnit) as EWeightUnit[], "Invalid weight unit")
      .required("Weight unit is required"),

    // Null as well as empty is "the pack states none": a product whose origin
    // was never read comes back with nothing here, and a form that refuses null
    // cannot be saved at all.
    countryOfManufactureISO: yup
      .string()
      .transform((value) => (value == null || value.trim() === "" ? undefined : value))
      .trim()
      .uppercase()
      .length(2, "Country of manufacture ISO must be exactly 2 characters")
      .optional(),

    countryOfRegistrationISO: yup
      .string()
      .transform((value) => (value == null || value.trim() === "" ? undefined : value))
      .trim()
      .uppercase()
      .length(2, "Country of registration ISO must be exactly 2 characters")
      .required(),

    barcode: yup.string().trim().required("Barcode is required"),

    price: yup
      .number()
      .transform(emptyToUndefined)
      .typeError("Price must be a number")
      .min(0.01, "Price must be greater than 0")
      .required("Price is required"),
  })
  .test(
    "weight-or-estimatedWeight",
    "Either weight or estimated weight is required",
    function (value) {
      if (!value) return false;

      const hasWeight = value.weight != null;
      const hasEstimatedWeight = value.estimatedWeight != null;

      if (hasWeight || hasEstimatedWeight) return true;

      return this.createError({
        path: "weight",
        message: "Weight is required when estimated weight is empty",
      });
    }
  )
  .required();

// The same schema for the section that edits the pack's particulars and not the
// two descriptions. Those are written in their own section, and a form that
// never shows them cannot ask for them: it left Save doing nothing on every
// product whose description was still empty, with nothing on screen to say why.
// The descriptions still travel with the save - the endpoint writes the whole
// translation - so that section fills them in from the product rather than from
// its own fields.
export const productParticularsSchema = updateProductInfoSchema.shape({
  translations: yup
    .object({
      ar: translationSchema.shape({
        description: yup.string().defined().default(''),
        languageCode: yup
          .mixed<ELanguageCode>()
          .oneOf([ELanguageCode.AR] as ELanguageCode[], "Arabic translation must use 'ar'")
          .required("Language code is required"),
      }),
      en: translationSchema.shape({
        description: yup.string().defined().default(''),
        languageCode: yup
          .mixed<ELanguageCode>()
          .oneOf([ELanguageCode.EN] as ELanguageCode[], "English translation must use 'en'")
          .required("Language code is required"),
      }),
    })
    .required("Translations are required"),
}) as yup.ObjectSchema<UpdateProductInfoFormValues>;

export const updateProductInfoDefaultValues: DefaultValues<UpdateProductInfoFormValues> = {
  translations: {
    ar: {
      name: "",
      description: "",
      languageCode: ELanguageCode.AR,
    },
    en: {
      name: "",
      description: "",
      languageCode: ELanguageCode.EN,
    },
  },
  barcode: "",
  countryOfManufactureISO: "",
  countryOfRegistrationISO: "",
  weight: undefined,
  estimatedWeight: undefined,
  drainedWeight: undefined,
  weightUnit: undefined,
  price: undefined,
};